import { StateCreator } from 'zustand';

import { createAppStore } from '../zustand';
import {
  WishListDetailsGlobalStoreDataType,
  WishListDetailsGlobalStoreType,
  WishListItemStoreType,
  WishListItemRawType,
  WishListCustomerWishlistType,
} from './wishListDetailsGlobalStore.types';

export const wishListDetailsGlobalStoreInitialState: WishListDetailsGlobalStoreDataType = {
  wishlist: [],
  isWishListDetailsSaved: false,
  hasFetchedWishListDetails: false,
  error: undefined,
};

/**
 * Normalize a wishlist item to ensure defaults for properties and wishlistSavedCount.
 */
const normalizeWishListItem = (item: Partial<WishListItemStoreType>): WishListItemStoreType => ({
  ...item,
  properties: item.properties ?? [],
  wishlistSavedCount: item.wishlistSavedCount ?? item.properties?.length ?? 0,
});

/**
 * Transform raw UXL wishlist data to simplified store format.
 */
export const transformUxlToStoreWishlist = (rawItems: WishListItemRawType[]): WishListItemStoreType[] => {
  return rawItems.map(raw => ({
    wishlistId: raw.id ?? undefined,
    wishlistName: raw.name ?? undefined,
    startDate: raw.startDate ?? undefined,
    endDate: raw.endDate ?? undefined,
    numberOfRooms: raw.numberOfRooms,
    numberOfGuests: raw.numberOfGuests,
    wishlistSavedCount: raw.properties?.length ?? 0,
    properties: (raw.properties ?? []).map(prop => ({
      propertyId: prop.id ?? undefined,
      images: (prop.media?.photoGallery?.features?.edges ?? [])
        .map(edge => edge?.node?.imageUrls?.square)
        .filter((url): url is string => !!url)
        .map(url => ({ url })),
    })),
  }));
};

const wishListDetailsGlobalStore: StateCreator<WishListDetailsGlobalStoreType> = set => ({
  ...wishListDetailsGlobalStoreInitialState,

  setWishListDetailsInfo: (value?: Partial<WishListDetailsGlobalStoreDataType>) => {
    set(prev => {
      const normalizedWishlist = (value?.wishlist ?? prev.wishlist).map(normalizeWishListItem);
      return {
        ...prev,
        ...value,
        wishlist: normalizedWishlist,
        isWishListDetailsSaved: normalizedWishlist.length > 0,
      };
    });
  },

  setPartialWishListDetailsInfo: (data: Partial<WishListDetailsGlobalStoreDataType>) => {
    set(prev => {
      const mergedWishlist = data.wishlist ?? prev.wishlist;
      const normalizedWishlist = mergedWishlist.map(normalizeWishListItem);
      return {
        ...prev,
        ...data,
        wishlist: normalizedWishlist,
        isWishListDetailsSaved: normalizedWishlist.length > 0,
      };
    });
  },

  resetWishListDetailsInfo: () => {
    set({ ...wishListDetailsGlobalStoreInitialState });
  },
});

export const useWishListDetailsGlobalStore = createAppStore(wishListDetailsGlobalStore, {
  usePersistentStore: false,
  persistentStoreName: 'WishListDetailsGlobalStore',
});

export const resetWishListDetails = () => {
  const { resetWishListDetailsInfo } = useWishListDetailsGlobalStore.getState();
  resetWishListDetailsInfo();
};

/**
 * Get wishlist details from store or fetch via provided fetcher.
 * Returns the store state after fetching.
 */
export const getWishListDetailsInfo = async (
  forceFetch: boolean = false,
  fetcher: () => Promise<WishListCustomerWishlistType>
): Promise<WishListDetailsGlobalStoreDataType> => {
  const state = useWishListDetailsGlobalStore.getState();

  if (!forceFetch && state.isWishListDetailsSaved) {
    return state;
  }

  const rawData = await fetcher();
  const transformedWishlist = transformUxlToStoreWishlist(rawData?.wishlists ?? []);

  const { setWishListDetailsInfo } = useWishListDetailsGlobalStore.getState();
  setWishListDetailsInfo({
    wishlist: transformedWishlist,
    hasFetchedWishListDetails: true,
  });

  return useWishListDetailsGlobalStore.getState();
};

/**
 * Fire-and-forget version of getWishListDetailsInfo.
 */
export const getWishListDetails = (
  forceFetch: boolean = false,
  fetcher: () => Promise<WishListCustomerWishlistType>
): void => {
  getWishListDetailsInfo(forceFetch, fetcher).catch(() => {
    // Errors are already handled inside getWishListDetailsInfo via setWishListDetailsInfo.
  });
};
